import React, { useEffect, useState } from "react";
import { Loader2, Download, Eye, AlertTriangle, CheckCircle2, Copy } from "lucide-react";
import { adminApi } from "../../lib/api";
import { Button } from "../ui/button";
import { useToast } from "../../hooks/use-toast";

// The latest batch of answers CASS supplied. Imported as drafts into the
// review queue — never published, never overwriting an existing answer.
export default function AskCassieNewAnswers() {
  const { toast } = useToast();
  const [status, setStatus] = useState(null);
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState("");

  const load = () => adminApi.askCassie.newAnswersStatus().then(setStatus).catch(() => setStatus(null));
  useEffect(() => { load(); }, []);

  const run = async (dry) => {
    setBusy(dry ? "dry" : "import");
    try {
      const r = await adminApi.askCassie.newAnswersImport(dry);
      setReport(r);
      if (!dry) {
        await load();
        toast({ title: `${r.imported.length} answers imported as drafts`, description: "Approve them in the Answers tab — nothing is live yet." });
      }
    } catch {
      toast({ title: "Couldn't import the batch", variant: "destructive" });
    } finally { setBusy(""); }
  };

  if (!status) return <p className="text-sm text-slate-400">Loading…</p>;

  return (
    <div data-testid="ask-cassie-new-answers">
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 mb-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="font-display font-bold text-navy">New answers batch — {status.source_file}</h3>
            <p className="text-xs text-slate-500 mt-1 leading-relaxed max-w-3xl">
              {status.total} answers written for the gaps in the library. They come in as <strong>drafts</strong>, so
              they're out of Google and off the public hub until you approve them in the Answers tab. Nothing existing
              is overwritten, and running this twice imports nothing the second time — so the same button brings them
              into the live site.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button onClick={() => run(true)} disabled={!!busy} variant="outline" className="rounded-full" data-testid="new-answers-preview">
              {busy === "dry" ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Eye size={14} className="mr-1.5" />} Preview
            </Button>
            <Button onClick={() => run(false)} disabled={!!busy || status.to_import === 0} className="bg-orange hover:bg-orange-dark text-white rounded-full" data-testid="new-answers-import">
              {busy === "import" ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Download size={14} className="mr-1.5" />} Import {status.to_import} as drafts
            </Button>
          </div>
        </div>

        <div className="mt-5 grid sm:grid-cols-3 gap-3" data-testid="new-answers-stats">
          {[
            { label: "In the batch", value: status.total },
            { label: "Already in the library", value: status.already_in_library },
            { label: "Still to import", value: status.to_import },
          ].map((s) => (
            <div key={s.label} className="rounded-xl bg-slate-50 p-4">
              <p className="text-xl font-display font-bold text-navy">{s.value}</p>
              <p className="text-xs text-slate-500 mt-0.5">{s.label}</p>
            </div>
          ))}
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          {Object.entries(status.by_category).map(([k, n]) => (
            <span key={k} className="text-xs font-semibold bg-navy/5 text-navy px-3 py-1.5 rounded-full">{k} · {n}</span>
          ))}
        </div>

        {status.to_import === 0 && (
          <p className="mt-4 text-xs text-teal flex items-center gap-1.5">
            <CheckCircle2 size={14} /> The whole batch is already in this library.
          </p>
        )}
      </div>

      {report && (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6" data-testid="new-answers-report">
          <h3 className="font-display font-bold text-navy">
            {report.dry_run ? "Preview — nothing was imported" : "Imported as drafts"}
          </h3>
          <div className="mt-3 grid sm:grid-cols-4 gap-3 text-xs">
            <p className="rounded-xl bg-slate-50 p-3"><strong className="text-navy">{report.imported.length}</strong> to import</p>
            <p className="rounded-xl bg-slate-50 p-3"><strong className="text-navy">{report.skipped.length}</strong> already there</p>
            <p className="rounded-xl bg-slate-50 p-3"><strong className="text-navy">{report.close_matches.length}</strong> close to an existing answer</p>
            <p className="rounded-xl bg-slate-50 p-3"><strong className="text-navy">{report.flagged.length}</strong> hold unverified wording</p>
          </div>

          {report.close_matches.length > 0 && (
            <div className="mt-5" data-testid="new-answers-close-matches">
              <p className="text-xs font-bold uppercase tracking-wider text-orange flex items-center gap-1.5">
                <Copy size={13} /> Check these against what's already written
              </p>
              {report.close_matches.map((c) => (
                <div key={c.slug} className="mt-2 rounded-xl border border-slate-200 p-3 text-xs">
                  <p className="font-semibold text-navy">{c.question}</p>
                  <p className="text-slate-500 mt-1">
                    Close to “{c.existing_question}” <span className="text-slate-400">({c.existing_slug} · {Math.round(c.overlap * 100)}% word overlap)</span>
                  </p>
                </div>
              ))}
            </div>
          )}

          {report.flagged.length > 0 && (
            <div className="mt-5" data-testid="new-answers-flagged">
              <p className="text-xs font-bold uppercase tracking-wider text-red-500 flex items-center gap-1.5">
                <AlertTriangle size={13} /> Don't publish until CASS confirms the wording
              </p>
              {report.flagged.map((f) => (
                <div key={f.slug} className="mt-2 rounded-xl border border-red-100 bg-red-50/40 p-3 text-xs">
                  <p className="font-semibold text-navy">{f.question}</p>
                  <p className="text-slate-500 mt-1">Matched: {f.matched.join(", ")}</p>
                </div>
              ))}
            </div>
          )}

          <div className="mt-5 max-h-64 overflow-y-auto">
            {report.imported.map((i) => (
              <p key={i.slug} className="text-xs text-slate-600 py-1.5 border-b border-slate-100 last:border-0">
                <span className="font-mono text-[11px] text-orange mr-2">{i.ref}</span>{i.question}
                <span className="text-slate-400"> — {i.category}</span>
              </p>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
